import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { App, Col, DatePicker, Form, InputNumber, Modal, Row } from "antd";

import { apiConfirmOptionBuy } from "api/apiOptionStrategies";
import { formatMoney, tradeMultiplier } from "./optionTradeUtils";
import { marketToday, utcDay } from "./optionStrategyUtils";

// Accept a pending buy signal with the real fill. The suggested contract can be
// corrected if a different one was bought.
const ConfirmOptionBuyModal = (props) => {
  const { trade, onClose, onSaved } = props;
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const premium = Form.useWatch("premium", form);
  const contracts = Form.useWatch("contracts", form);
  const userPremiumFee = useSelector(
    (state) => state.main.user.preferences?.premiumFee,
  );
  const option = trade.optionTrade;
  const cost = (premium || 0) * (contracts || 0) * tradeMultiplier(trade);

  const onFinish = async (values) => {
    setIsSaving(true);
    try {
      await apiConfirmOptionBuy({
        tradeId: trade._id,
        contracts: values.contracts,
        premium: values.premium,
        fees: values.fees,
        openDate: values.openDate.format("YYYY-MM-DD"),
        strikePrice: values.strikePrice,
        expirationDate: values.expirationDate.format("YYYY-MM-DD"),
        iv: values.ivPercent != null ? values.ivPercent / 100 : undefined,
      });
      message.success(`${trade.symbol} position opened`);
      onSaved();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to confirm buy");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={`Confirm buy - ${trade.symbol} ${option.strikePrice} call`}
      open
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Confirm buy"
      confirmLoading={isSaving}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          contracts: trade.advicedBuyAmount,
          premium: trade.advicedBuyPrice,
          fees: userPremiumFee ?? 0,
          openDate: marketToday(),
          strikePrice: option.strikePrice,
          expirationDate: utcDay(option.expirationDate),
          ivPercent:
            option.ivAtOpen != null
              ? +(option.ivAtOpen * 100).toFixed(1)
              : undefined,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="contracts"
              label="Contracts"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber min={1} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="premium"
              label="Fill price per share"
              extra={`Cost: $${formatMoney(cost)} (suggested ask ${trade.advicedBuyPrice})`}
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber min={0.01} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="fees" label="Commission">
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="openDate"
              label="Open date"
              rules={[{ required: true, message: "Required" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="strikePrice"
              label="Strike"
              rules={[{ required: true, message: "Required" }]}
            >
              <InputNumber min={0} step={0.5} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="expirationDate"
              label="Expiration"
              rules={[{ required: true, message: "Required" }]}
            >
              <DatePicker style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="ivPercent" label="IV at open">
              <InputNumber
                min={0}
                step={0.1}
                addonAfter="%"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

ConfirmOptionBuyModal.propTypes = {
  trade: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default ConfirmOptionBuyModal;
